import { useState } from 'react';
import styled from 'styled-components';
import {
  Avatar,
  Flex,
  FlexColumn,
  IconButton,
} from '@elementary/common-ui/components';
import {
  HEADER_HEIGHT,
  HEADER_MENU_VERTICAL_OFFSET,
} from '@elementary/common-ui/constants/constants';
import useAccountPermission from '@elementary/common-ui/hooks/useAccountPermission';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { Divider, Menu, MenuItem, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useLogout from 'shared/hooks/useLogout';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useEnvironments from 'shared/hooks/useEnvironments';

const MenuStyled = styled(Menu)`
  .MuiList-root {
    padding: 8px 0;
  }
`;

const HeaderLabel = styled(Typography)`
  font-size: 14px;
  line-height: normal;
`;

const Title = styled(Typography)`
  font-size: 12px;
  line-height: normal;
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  padding: 4px 16px;
  color: ${({ theme }) => theme.palette.common.gray11};
`;

const MenuItemStyled = styled(MenuItem)`
  font-size: 14px;
`;

const AvatarIconButton = styled(IconButton)`
  width: ${HEADER_HEIGHT}px;
  min-height: ${HEADER_HEIGHT}px;
  border-radius: 0 !important;

  &:hover {
    background: none;
  }

  .MuiAvatar-root {
    transition: opacity 0.15s ease;
  }

  &:hover .MuiAvatar-root {
    opacity: 0.8;
  }
`;

type Props = {
  userId?: string;
  username?: string;
  email?: string;
  account?: string;
  deactivated?: boolean;
};

const AccountMenu = ({
  userId,
  username,
  email,
  account,
  deactivated,
}: Props) => {
  const [menuAnchor, setMenuAnchor] = useState<any>();
  const hasAccountPermission = useAccountPermission();
  const hasEnvPermission = useEnvPermission();
  const logout = useLogout();
  const navigate = useNavigate();
  const { data: accountData } = useAccount();
  const { hasNoEnvironmentAccess } = useEnvironments();

  const handleAccountMenuItemClick = (value: string) => {
    switch (value) {
      case 'profile':
        navigate('/settings/profile');
        break;
      case 'tokens':
      case 'user-tokens':
        navigate('/settings/user-tokens');
        break;
      case 'gpg':
        navigate('/settings/gpg');
        break;
      case 'users':
        navigate('/settings/users');
        break;
      case 'roles':
        if (accountData?.roles_and_permissions_enabled) {
          navigate('/settings/roles');
        }
        break;
      case 'groups':
        if (accountData?.has_scim_configuration) {
          navigate('/settings/groups');
        }
        break;
      case 'settings':
        navigate('/settings/account');
        break;
      case 'account-tokens':
        navigate('/settings/account-tokens');
        break;
      case 'logs':
        navigate('/settings/logs');
        break;
      case 'ai-usage':
        navigate('/settings/ai-usage');
        break;
      case 'logout':
        logout();
        break;
      default:
        break;
    }
  };

  const showAccountSection =
    hasAccountPermission('VIEW_USERS') || hasAccountPermission('EDIT_USERS');

  if (!userId || !username) {
    return null;
  }

  return (
    <>
      <AvatarIconButton onClick={(e) => setMenuAnchor(e.currentTarget)}>
        <Avatar value={{ id: userId, name: username }} size="large" />
      </AvatarIconButton>
      <MenuStyled
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        onClick={(e: any) => {
          handleAccountMenuItemClick(e.target.id);
          setMenuAnchor(null);
        }}
        anchorOrigin={{
          vertical: HEADER_MENU_VERTICAL_OFFSET,
          horizontal: 'right',
        }}
      >
        <Flex sx={{ alignItems: 'center', p: 2, gap: 2 }}>
          <Avatar value={{ id: userId, name: username }} size="large" />
          <FlexColumn sx={{ gap: 1 }}>
            <HeaderLabel>{email}</HeaderLabel>
            <HeaderLabel sx={{ color: 'common.gray2' }}>{account}</HeaderLabel>
            {deactivated && (
              <Typography sx={{ fontSize: '12px', color: 'common.red100' }}>
                Deactivated
              </Typography>
            )}
          </FlexColumn>
        </Flex>
        <Divider sx={{ m: '8px 0' }} />
        <FlexColumn sx={{ gap: 1 }}>
          <Title>USER</Title>
          <MenuItemStyled id="profile">My Profile</MenuItemStyled>
          <MenuItemStyled id="user-tokens">Personal Tokens</MenuItemStyled>
          <MenuItemStyled id="gpg">GPG Keys</MenuItemStyled>
          {showAccountSection && <Title>ACCOUNT</Title>}
          {hasAccountPermission('VIEW_USERS') && (
            <MenuItemStyled id="users">Users</MenuItemStyled>
          )}
          {hasAccountPermission('VIEW_ROLES') &&
            accountData?.roles_and_permissions_enabled && (
              <MenuItemStyled id="roles">Roles</MenuItemStyled>
            )}
          {hasAccountPermission('VIEW_GROUPS') &&
            accountData?.has_scim_configuration && (
              <MenuItemStyled id="groups">Groups</MenuItemStyled>
            )}
          {hasAccountPermission('EDIT_USERS') && (
            <MenuItemStyled id="account-tokens">Account Tokens</MenuItemStyled>
          )}
          {hasAccountPermission('EDIT_USERS') && (
            <MenuItemStyled id="settings">Settings</MenuItemStyled>
          )}
          {hasEnvPermission('VIEW') && !hasNoEnvironmentAccess && (
            <MenuItemStyled id="logs">Logs</MenuItemStyled>
          )}
          {hasAccountPermission('VIEW_AI_USAGE') && (
            <MenuItemStyled id="ai-usage">AI Usage</MenuItemStyled>
          )}
        </FlexColumn>
        <Divider sx={{ m: '8px 0' }} />
        <MenuItemStyled id="logout">Sign out</MenuItemStyled>
      </MenuStyled>
    </>
  );
};
export default AccountMenu;
